import { Button, Box, Card, CardContent, Typography } from "@mui/material";
import { Helmet } from "react-helmet-async";
import MicrosoftLogo from "../../assets/images/Microsoft.png";
import BrandLogo from "../../components/shell/BrandLogo";
import { Callout } from "../../components/ui/Callout";
import { useMsal } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";

import { loginRequest } from "../../authConfig";
import { useEffect, useCallback, useRef } from "react";
import { setSession } from "../../utils/jwt";
import { InteractionRequiredAuthError, AuthError } from "@azure/msal-browser";

export default function LoginPage() {
  const { instance, accounts, inProgress } = useMsal();
  const tokenAcquired = useRef(false);

  const getToken = useCallback(async () => {
    if (accounts.length === 0 || tokenAcquired.current) return;

    try {
      const response = await instance.acquireTokenSilent({
        ...loginRequest,
        account: accounts[0],
        scopes: [process.env.REACT_APP_API_SCOPE].filter(
          (s): s is string => typeof s === "string"
        ),
      });
      setSession(response.accessToken);
      tokenAcquired.current = true;
    } catch (e) {
      if (e instanceof InteractionRequiredAuthError) {
        console.log("Interaction required - user needs to log in again");
        // Don't automatically redirect here - let user click the button
        tokenAcquired.current = false;
      } else if (e instanceof AuthError) {
        console.error("AuthError details:", e.errorCode, e.errorMessage);
      } else {
        console.error("Token error:", e);
      }
    }
  }, [accounts, instance]);

  const handleRedirect = async () => {
    if (inProgress !== InteractionStatus.None) return;
    await instance.loginRedirect({
      ...loginRequest,
    });
  };

  // After redirect completes or when user is already signed in, try to get token silently
  useEffect(() => {
    if (inProgress === InteractionStatus.None && accounts.length > 0) {
      void getToken();
    }
  }, [inProgress, accounts.length, getToken]);

  return (
    <Box
      component="main"
      sx={{
        flex: 1,
        bgcolor: "background.default",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "18px",
        px: 5,
        pt: { xs: 8, sm: "34px" },
        pb: { xs: 8, sm: "44px" },
      }}
    >
      <Helmet title="Sign in" />
      <Box
        sx={{
          display: "grid",
          justifyItems: "center",
          gap: 2.5,
          width: "100%",
          maxWidth: 480,
        }}
      >
        <BrandLogo height={46} />
        <Typography
          component="p"
          sx={{
            fontSize: "0.8125rem",
            fontWeight: 500,
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            color: "text.secondary",
          }}
        >
          CSI Pay
        </Typography>
      </Box>
      <Card sx={{ width: "100%", maxWidth: 400 }}>
        <CardContent
          sx={{
            display: "grid",
            gap: "14px",
            p: "26px 28px",
            "&:last-child": { pb: "26px" },
          }}
        >
          <Typography
            variant="h2"
            component="h1"
            sx={{ fontSize: "1.3125rem", fontWeight: 500 }}
          >
            Sign in to CSI Pay
          </Typography>
          <Typography sx={{ fontSize: "0.84375rem", color: "text.secondary" }}>
            For Care Solutions staff. Use your Microsoft work account.
          </Typography>
          <Button
            variant="contained"
            color="primary"
            fullWidth
            sx={{ height: 40 }}
            startIcon={
              <img src={MicrosoftLogo} alt="" width={21} height={21} />
            }
            onClick={handleRedirect}
            disabled={inProgress !== InteractionStatus.None}
          >
            Sign in with Microsoft
          </Button>
          <Callout tone="info" title="Can't sign in?">
            Ask a CSI Pay administrator to add your account.
          </Callout>
        </CardContent>
      </Card>
    </Box>
  );
}
